import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getServices, subscribeServices } from '../firebase/firestore';
import { Service, Period } from '../types';
import { getDateRange, formatCurrency } from '../utils/helpers';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Tour from '../components/Tour';
import LoadingOverlay from '../components/LoadingOverlay';
import AdsSection from '../components/AdsSection';
import '../styles/Dashboard.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCog, faSyncAlt, faExclamationTriangle, faStar } from '@fortawesome/free-solid-svg-icons';

const Dashboard: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [period, setPeriod] = useState<Period>('monthly');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTour, setShowTour] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const checkFirstAccess = useCallback(() => {
    const tourCompleted = localStorage.getItem('tour_completed');
    if (!tourCompleted) {
      setShowTour(true);
    }
  }, []);

  const loadServices = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { start, end } = getDateRange(period);
      const data = await getServices(user.uid, start, end);
      
      // Salvar em localStorage como fallback
      if (data && data.length > 0) {
        // Não salvar em localStorage; salvamento ocorre apenas no Firestore
      }
      setServices(data);
    } catch (error: any) {
      console.error('Erro ao carregar serviços:', error);
      setError(error.message || 'Erro ao carregar dados.');
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [user, period]);

  useEffect(() => {
    if (!user) return;

    loadServices();
    checkFirstAccess();

    const unsubscribe = subscribeServices(user.uid, (data) => {
      setServices(data);
      setLoading(false);
    }, (err) => {
      console.warn('Falha no snapshot de serviços:', err);
    });

    return () => {
      unsubscribe();
    };
  }, [user, loadServices, checkFirstAccess]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const totalServices = services.length;
  const totalRevenue = services.length > 0 ? services.reduce((sum, s) => sum + s.value, 0) : 0;
  const totalProfit = services.length > 0 ? services.reduce((sum, s) => sum + (s.value * (s.profitMargin / 100)), 0) : 0;
  const avgMargin = services.length > 0 
    ? services.reduce((sum, s) => sum + s.profitMargin, 0) / services.length 
    : 0;

  const chartData = services.slice(0, 7).reverse().map(s => ({
    date: new Date(s.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
    valor: s.value,
    lucro: s.value * (s.profitMargin / 100)
  }));

  const daysRemaining = user?.trialEndDate 
    ? Math.ceil(((new Date(user.trialEndDate)).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : 0;

  return (
    <div className="dashboard">
      {showTour && (
        <Tour
          onComplete={() => setShowTour(false)}
          onSkip={() => setShowTour(false)}
        />
      )}

      <header className="dashboard-header">
        <div>
          <h1>Olá, {user?.displayName || 'Usuário'}! </h1>
          {!user?.isPremium && daysRemaining > 0 && (
            <p className="trial-badge">{daysRemaining} dias restantes</p>
          )}
        </div>
        <div className="header-actions">
          <Link to="/profile" className="profile-link">
            <img 
              src={user?.photoURL || 'https://via.placeholder.com/40'} 
              alt="Perfil" 
              className="header-avatar"
            />
          </Link>
          {/* Notifications bell */}
          <div style={{ marginRight: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
            {user && React.createElement(require('../components/NotificationsBell').default)}
            {/* Show enable notifications action when permission is default */}
            {user && typeof Notification !== 'undefined' && Notification.permission === 'default' && (
              <button className="action-btn" onClick={async () => {
                try {
                  const { requestAndSaveFcmToken } = await import('../firebase/messaging');
                  const token = await requestAndSaveFcmToken(user.uid);
                  if (token) alert('Notificações ativadas com sucesso'); else alert('Não foi possível ativar notificações (verifique permissões/VAPID key)');
                } catch (err) { console.error(err); alert('Erro ao ativar notificações'); }
              }}>Ativar notificações</button>
            )}
          </div>
          <button onClick={signOut} className="logout-btn">Sair</button>
        </div>
      </header>

      <div className="period-selector">
        {(['daily', 'weekly', 'monthly', 'yearly'] as Period[]).map(p => (
          <button
            key={p}
            className={period === p ? 'active' : ''}
            onClick={() => setPeriod(p)}
          >
            {p === 'daily' && 'Dia'}
            {p === 'weekly' && 'Semana'}
            {p === 'monthly' && 'Mês'}
            {p === 'yearly' && 'Ano'}
          </button>
        ))}
      </div>

      <LoadingOverlay show={loading} message="Carregando seu dashboard..." />
      
      {!loading && (
        <>
          {!isOnline && (
            <div className="error-banner" style={{
              background: '#ffe',
              border: '1px solid #ffb700',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '20px',
              color: '#b8860b'
            }}>
              <strong><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{verticalAlign:'middle', marginRight:6}}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg> Você está offline.</strong> Algumas funcionalidades podem estar indisponíveis. Os dados serão sincronizados assim que a conexão voltar.
            </div>
          )}
          
          {error && (
            <div className="error-banner" style={{
              background: '#fee',
              border: '1px solid #f88',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '20px',
              color: '#c33'
            }}>
              <strong><FontAwesomeIcon icon={faExclamationTriangle} /> Erro:</strong> {error}
              <br />
              <small>
                Verifique se o Firestore Database foi criado no{' '}
                <a 
                  href="https://console.firebase.google.com/project/my-money-51168/firestore" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#00c', textDecoration: 'underline' }}
                >
                  Firebase Console
                </a>
              </small>
            </div>
          )}
          
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-icon"></span>
              <div>
                <p className="stat-label">Receita Total</p>
                <p className="stat-value">{formatCurrency(totalRevenue)}</p>
              </div>
            </div>
            <div className="stat-card">
              <span className="stat-icon"></span>
              <div>
                <p className="stat-label">Lucro Total</p>
                <p className="stat-value green">{formatCurrency(totalProfit)}</p>
              </div>
            </div>
            <div className="stat-card">
              <span className="stat-icon"></span>
              <div>
                <p className="stat-label">Margem Média</p>
                <p className="stat-value">{avgMargin.toFixed(1)}%</p>
              </div>
            </div>
            <div onClick={() => navigate('/services')} className="stat-card clickable">
              <span className="stat-icon"></span>
              <div>
                <p className="stat-label">Lançamentos</p>
                <p className="stat-value">{totalServices}</p>
              </div>
            </div>
          </div>

          {chartData.length > 0 && (
            <div className="chart-container">
              <h3>Receita vs Lucro (últimos 7 lançamentos)</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value as number)} />
                  <Line type="monotone" dataKey="valor" stroke="#667eea" strokeWidth={2} name="Receita" />
                  <Line type="monotone" dataKey="lucro" stroke="#10b981" strokeWidth={2} name="Lucro" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="quick-actions">
            <Link to="/services" className="action-btn primary">
              <FontAwesomeIcon icon={faPlus} /> Adicionar Lançamento
            </Link>
            <Link to="/reports" className="action-btn">
               Ver Relatórios
            </Link>
            {!user?.isPremium && (
              <Link to="/premium" className="action-btn premium">
                <FontAwesomeIcon icon={faStar} /> Upgrade Premium
              </Link>
            )}
            {user?.email === 'jusanmagno@gmail.com' && (
              <Link to="/admin" className="action-btn admin">
                <FontAwesomeIcon icon={faCog} /> Área Administrativa
              </Link>
            )}
            <button
              className="action-btn sync"
              style={{ background: '#4F8EF7', color: '#fff', marginLeft: 8 }}
              onClick={async () => {
                if (!user) return;
                setLoading(true);
                try {
                  // Busca dados da nuvem
                  const { start, end } = getDateRange(period);
                  const cloudData = await getServices(user.uid, start, end);
                  // Busca dados do cache local
                  // Apenas sincroniza da nuvem. Não usa cache local como fonte de verdade.
                  const finalData = cloudData;
                  setServices(finalData);
                } catch (err) {
                  setError('Erro ao sincronizar dados.');
                }
                setLoading(false);
              }}
            > <FontAwesomeIcon icon={faSyncAlt} /> Sincronizar</button>
          </div>

          <AdsSection />
        </>
      )}
    </div>
  );
};

export default Dashboard;
